import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import {
    Flex,
    Text,
    Button,
    IconButton,
    Spinner,
    Box,
    Input,
    HStack,
    VStack,
    Badge,
    Switch,
    FormControl,
    FormLabel,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useDisclosure,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Textarea,
    Divider,
    Select,
    Tooltip,
} from "@chakra-ui/react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaPlusCircle, FaPencilAlt, FaTrashAlt, FaCheck, FaBan, FaSyncAlt } from "react-icons/fa";

function normEmail(v) {
    return (v || "").toString().trim().toLowerCase();
}

function WhiteListUser() {
    const toast = useToast();

    const apiBase = process.env.REACT_APP_API_URL;

    // list state
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [activeFilter, setActiveFilter] = useState("all"); // all | 1 | 0

    // settings
    const [useWhitelist, setUseWhitelist] = useState(false);
    const [savingSetting, setSavingSetting] = useState(false);

    // modal
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [operation, setOperation] = useState("insert"); // insert | update
    const [editRow, setEditRow] = useState(null);

    // form
    const [formEmail, setFormEmail] = useState("");
    const [formNote, setFormNote] = useState("");
    const [formIsActive, setFormIsActive] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const filterPayload = useMemo(() => {
        let is_active = "all";
        if (activeFilter === "1") is_active = 1;
        if (activeFilter === "0") is_active = 0;

        return {
            q: q?.trim() || "",
            is_active,
            limit: 1000,
            offset: 0,
        };
    }, [q, activeFilter]);

    useEffect(() => {
        loadSetting();
        loadList();
    }, []);

    const showSuccess = (title, desc) => {
        toast({
            title: title || "Success",
            description: desc || "",
            status: "success",
            duration: 2500,
            isClosable: true,
            position: "top",
        });
    };

    const showWarn = (title, desc) => {
        toast({
            title: title || "Warning",
            description: desc || "",
            status: "warning",
            duration: 3000,
            isClosable: true,
            position: "top",
        });
    };

    const showErr = (title, desc) => {
        toast({
            title: title || "Error",
            description: desc || "Cannot connect to the server!",
            status: "error",
            duration: 3500,
            isClosable: true,
            position: "top",
        });
    };

    const loadSetting = async () => {
        try {
            const response = await axios.post(`${apiBase}/whitelist/setting_get_use_whitelist`, {});
            const res = response.data;
            if (res && res.status === "success") {
                setUseWhitelist(!!res.value);
            } else {
                showWarn("Warning", res?.status || "Cannot load setting");
            }
        } catch (e) {
            showErr("Error", `${e}`);
        }
    };

    const toggleSetting = async (nextValue) => {
        try {
            setSavingSetting(true);
            const response = await axios.post(`${apiBase}/whitelist/setting_set_use_whitelist`, {
                value: nextValue ? 1 : 0,
            });
            const res = response.data;
            if (res && res.status === "success") {
                setUseWhitelist(!!res.value);
                showSuccess("Saved", `${res.value ? "เปิดการใช้งาน" : "ปิดการใช้งาน"} Email Whitelist (สถานะปัจจุบัน: ${res.value ? "ON" : "OFF"})`);
            } else {
                showWarn("Warning", res?.status || "Cannot save setting");
            }
        } catch (e) {
            showErr("Error", `${e}`);
        } finally {
            setSavingSetting(false);
        }
    };

    const loadList = async (overridePayload) => {
        try {
            setLoading(true);
            const payload = overridePayload || filterPayload;
            const response = await axios.post(`${apiBase}/whitelist/whitelist_list`, payload);
            const res = response.data;
            if (res && res.status === "success") {
                setRows(res.rows || []);
            } else {
                showWarn("Warning", res?.status || "Cannot load whitelist");
            }
        } catch (e) {
            showErr("Error", `${e}`);
        } finally {
            setLoading(false);
        }
    };

    const openAdd = () => {
        setOperation("insert");
        setEditRow(null);
        setFormEmail("");
        setFormNote("");
        setFormIsActive(true);
        onOpen();
    };

    const openEdit = (row) => {
        setOperation("update");
        setEditRow(row);
        setFormEmail(row?.email || "");
        setFormNote(row?.note || "");
        setFormIsActive(String(row?.is_active) === "1");
        onOpen();
    };

    const closeModal = () => {
        if (submitting) return;
        onClose();
    };

    const submitForm = async () => {
        const email = normEmail(formEmail);
        if (!email) {
            showWarn("Validation", "Email is required");
            return;
        }

        try {
            setSubmitting(true);

            if (operation === "insert") {
                const response = await axios.post(`${apiBase}/whitelist/whitelist_add`, {
                    email,
                    note: formNote,
                });
                const res = response.data;
                if (res && res.status === "success") {
                    showSuccess("Added", "Whitelist updated");
                    closeModal();
                    await loadList();
                } else {
                    showWarn("Warning", res?.status || "Cannot add");
                }
                return;
            }

            // update
            if (!editRow?.id) {
                showWarn("Warning", "Missing id");
                return;
            }

            const response = await axios.post(`${apiBase}/whitelist/whitelist_update`, {
                id: editRow.id,
                note: formNote,
                is_active: formIsActive ? 1 : 0,
            });
            const res = response.data;
            if (res && res.status === "success") {
                showSuccess("Updated", "Saved");
                closeModal();
                await loadList();
            } else {
                showWarn("Warning", res?.status || "Cannot update");
            }
        } catch (e) {
            showErr("Error", `${e}`);
        } finally {
            setSubmitting(false);
        }
    };

    const setActive = async (row, nextActive) => {
        try {
            const endpoint = nextActive ? "whitelist_enable" : "whitelist_disable";
            const response = await axios.post(`${apiBase}/whitelist/${endpoint}`, {
                id: row.id,
            });
            const res = response.data;
            if (res && res.status === "success") {
                showSuccess("Saved", nextActive ? "Enabled" : "Disabled");
                await loadList();
            } else {
                showWarn("Warning", res?.status || "Cannot update status");
            }
        } catch (e) {
            showErr("Error", `${e}`);
        }
    };

    const confirmDelete = (row) => {
        Swal.fire({
            title: "ยืนยันการลบ",
            text: `คุณต้องการลบ ${row.email} ใช่หรือไม่`,
            showCancelButton: true,
            denyButtonColor: "#6CB4EE",
            confirmButtonColor: "#ED0A3F",
            confirmButtonText: `ยืนยัน (ลบ)`,
            cancelButtonText: "ยกเลิก",
        }).then((result) => {
            if (result.isConfirmed) deleteRow(row);
        });
    };

    const deleteRow = async (row) => {
        try {
            const response = await axios.post(`${apiBase}/whitelist/whitelist_delete`, {
                id: row.id,
            });
            const res = response.data;
            if (res && res.status === "success") {
                showSuccess("Deleted", "Removed");
                await loadList();
            } else {
                showWarn("Warning", res?.status || "Cannot delete");
            }
        } catch (e) {
            showErr("Error", `${e}`);
        }
    };

    return (
        <Flex direction="column" pt={{ sm: "125px", lg: "100px" }}>
            <Flex w="100%" direction="column" p={4}>
                <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={3}>
                    <VStack align="start" spacing={0}>
                        <Text fontSize={20} fontWeight={700}>
                        เปิด/ปิด การ Login
                        </Text>
                        <Text fontSize="sm" opacity={0.7}>
                            เมื่อเปิดใช้ whitelist ระบบจะอนุญาติให้เข้าใช้งาน MyIPST โดยใช้อีเมลที่อยู่ใน whitelist เท่านั้น (ปิดการเข้าใช้งานระบบผ่าน Google และ SSO)
                        </Text>
                    </VStack>

                    <HStack spacing={2}>
                        <Button
                            leftIcon={<FaSyncAlt />}
                            variant="outline"
                            size="sm"
                            onClick={() => loadList()}
                            isDisabled={loading}
                        >
                            Refresh
                        </Button>
                        <Button
                            leftIcon={<FaPlusCircle />}
                            colorScheme="blue"
                            size="sm"
                            onClick={openAdd}
                        >
                            Add Email
                        </Button>
                    </HStack>
                </Flex>

                <Box borderWidth="1px" borderRadius="lg" p={4}>
                    <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                        <HStack spacing={3} flexWrap="wrap">
                            <FormControl display="flex" alignItems="center" w="auto">
                                <FormLabel mb="0" fontSize="sm" fontWeight={700}>
                                    ใช้ Email Whitelist
                                </FormLabel>
                                <Switch
                                    isChecked={useWhitelist}
                                    isDisabled={savingSetting}
                                    onChange={(e) => toggleSetting(e.target.checked)}
                                />
                            </FormControl>

                            {useWhitelist ? (
                                <Badge colorScheme="green" variant="subtle">
                                    ON
                                </Badge>
                            ) : (
                                <Badge colorScheme="gray" variant="subtle">
                                    OFF
                                </Badge>
                            )}
                        </HStack>

                        <HStack spacing={2} flexWrap="wrap">
                            <Input
                                placeholder="Search email / note"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        loadList(); // ใช้ filterPayload ล่าสุด (q + activeFilter)
                                    }
                                }}
                                w={{ base: "220px", md: "320px" }}
                            />

                            <FormControl w="160px">
                                <Select
                                    value={activeFilter}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setActiveFilter(v);

                                        // ค้นหาทันทีด้วยค่าใหม่ (ไม่รอ state update)
                                        let is_active = "all";
                                        if (v === "1") is_active = 1;
                                        if (v === "0") is_active = 0;

                                        loadList({
                                            q: q?.trim() || "",
                                            is_active,
                                            limit: 1000,
                                            offset: 0,
                                        });
                                    }}
                                >
                                    <option value="all">All status</option>
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </Select>

                            </FormControl>


                            <Button
                                size="sm"
                                variant="outline"
                                colorScheme="blue"
                                onClick={() => loadList({ ...filterPayload, q })}
                                isDisabled={loading}
                            >
                                Search
                            </Button>
                        </HStack>
                    </Flex>

                    <Divider my={4} />

                    {loading ? (
                        <Flex justify="center" py={6}>
                            <HStack>
                                <Spinner />
                                <Text>Loading...</Text>
                            </HStack>
                        </Flex>
                    ) : (
                        <Box overflowX="auto">
                            <Table size="sm">
                                <Thead>
                                    <Tr>
                                        <Th w="60px">#</Th>
                                        <Th>Email</Th>
                                        <Th>Note</Th>
                                        <Th w="120px">Status</Th>
                                        <Th w="190px">Updated</Th>
                                        <Th w="170px" textAlign="right">
                                            Actions
                                        </Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {rows.length === 0 && (
                                        <Tr>
                                            <Td colSpan={6}>
                                                <Text opacity={0.7} py={3}>
                                                    No data
                                                </Text>
                                            </Td>
                                        </Tr>
                                    )}

                                    {rows.map((r, idx) => {
                                        const isActive = String(r.is_active) === "1";
                                        return (
                                            <Tr key={r.id || idx}>
                                                <Td>{idx + 1}</Td>
                                                <Td>
                                                    <Text fontWeight={700}>{r.email}</Text>
                                                </Td>
                                                <Td>
                                                    <Text fontSize="sm" opacity={0.85} noOfLines={2}>
                                                        {r.note || "-"}
                                                    </Text>
                                                </Td>
                                                <Td>
                                                    {isActive ? (
                                                        <Badge colorScheme="green">ACTIVE</Badge>
                                                    ) : (
                                                        <Badge colorScheme="red">INACTIVE</Badge>
                                                    )}
                                                </Td>
                                                <Td>
                                                    <Text fontSize="sm" opacity={0.75}>
                                                        {r.updated_at
                                                            ? moment.tz(r.updated_at, "Asia/Bangkok").format("DD MMM YYYY HH:mm")
                                                            : "-"
                                                        }
                                                    </Text>
                                                </Td>
                                                <Td>
                                                    <HStack justify="flex-end" spacing={1}>
                                                        <IconButton
                                                            size="sm"
                                                            aria-label="edit"
                                                            icon={<FaPencilAlt />}
                                                            onClick={() => openEdit(r)}
                                                        />
                                                        {isActive ? (
                                                            <Tooltip label="Disable this user">
                                                                <IconButton
                                                                    size="sm"
                                                                    aria-label="disable"
                                                                    icon={<FaBan />}
                                                                    onClick={() => setActive(r, false)}
                                                                />
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip label="Enable this user">
                                                                <IconButton
                                                                    size="sm"
                                                                    aria-label="enable"
                                                                    icon={<FaCheck />}
                                                                    onClick={() => setActive(r, true)}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                        <IconButton
                                                            size="sm"
                                                            aria-label="delete"
                                                            icon={<FaTrashAlt />}
                                                            colorScheme="red"
                                                            variant="outline"
                                                            onClick={() => confirmDelete(r)}
                                                        />
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    )}
                </Box>
            </Flex>

            {/* Add/Edit Modal */}
            <Modal isOpen={isOpen} onClose={closeModal} isCentered size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {operation === "insert" ? "Add Whitelist Email" : "Edit Whitelist Email"}
                    </ModalHeader>
                    <ModalCloseButton isDisabled={submitting} />
                    <ModalBody>
                        <VStack align="stretch" spacing={3}>
                            <Box>
                                <Text fontSize="sm" fontWeight={700} mb={1}>
                                    Email
                                </Text>
                                <Input
                                    placeholder="someone@example.com"
                                    value={formEmail}
                                    onChange={(e) => setFormEmail(e.target.value)}
                                    isDisabled={operation === "update"} // ป้องกันชน UNIQUE key + ให้แก้ผ่าน delete/add แทน
                                />
                                {operation === "update" && (
                                    <Text fontSize="xs" opacity={0.65} mt={1}>
                                        Email cannot be changed (delete & add if needed)
                                    </Text>
                                )}
                            </Box>

                            <Box>
                                <Text fontSize="sm" fontWeight={700} mb={1}>
                                    Note
                                </Text>
                                <Textarea
                                    placeholder="note (optional)"
                                    value={formNote}
                                    onChange={(e) => setFormNote(e.target.value)}
                                    rows={3}
                                />
                            </Box>

                            {operation === "update" && (
                                <FormControl display="flex" alignItems="center">
                                    <FormLabel mb="0" fontSize="sm" fontWeight={700}>
                                        Active
                                    </FormLabel>
                                    <Switch
                                        isChecked={formIsActive}
                                        onChange={(e) => setFormIsActive(e.target.checked)}
                                    />
                                </FormControl>
                            )}
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={2} onClick={closeModal} isDisabled={submitting}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={submitForm}
                            isLoading={submitting}
                            loadingText="Saving"
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Flex>
    );
}

export default WhiteListUser;
